import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Chip,
  Alert,
} from "@mui/material";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import QrCodeIcon from "@mui/icons-material/QrCode";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import CheckIcon from "@mui/icons-material/Check";
import LaunchIcon from "@mui/icons-material/Launch";
import EditIcon from "@mui/icons-material/Edit";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import AutorenewIcon from "@mui/icons-material/Autorenew";

const STOC_MANAGER_API = "https://server-9p4hx.kinsta.app/api/stocmanager";

const getErrorMessage = (error, fallback) => {
  const responseError = error?.response?.data?.error;
  if (typeof responseError === "string" && responseError.trim())
    return responseError;
  if (responseError?.message) return responseError.message;
  if (error?.code === "ECONNABORTED") {
    return "Sincronizarea a durat prea mult. Verifică conexiunea și încearcă din nou.";
  }
  return fallback;
};

const getSyncUpdates = (data) => {
  const payload =
    data?.product || data?.data?.product || data?.data || data || {};
  const webLink = payload.web_link ?? payload.permalink;
  const imageUrl =
    payload.image_url ?? payload.image ?? payload.images?.[0]?.src;
  const price = payload.price ?? payload.df_price ?? payload.regular_price;
  const updates = {};
  if (String(webLink || "").trim()) updates.web_link = webLink;
  if (String(imageUrl || "").trim()) updates.image_url = imageUrl;
  if (price !== undefined && price !== null && String(price).trim()) {
    updates.price = price;
  }
  return updates;
};

const syncProductWithRetry = async (productId, sku) => {
  for (let attempt = 1; attempt <= 2; attempt += 1) {
    try {
      return await axios.post(
        `${STOC_MANAGER_API}/products/${encodeURIComponent(productId)}/sync`,
        { sku },
        { timeout: 50000 },
      );
    } catch (error) {
      const status = Number(error?.response?.status);
      const transient =
        ["ECONNABORTED", "ETIMEDOUT", "ERR_NETWORK", "ECONNRESET"].includes(
          error?.code,
        ) ||
        status === 408 ||
        status === 425 ||
        status === 429 ||
        status >= 500;
      if (attempt === 2 || !transient) throw error;
      await new Promise((resolve) =>
        setTimeout(resolve, 500 * 2 ** (attempt - 1)),
      );
    }
  }
};

function MobileProductsInventoryDetails() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [availableFolders, setAvailableFolders] = useState([]);
  const [targetFolderId, setTargetFolderId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [folderPath, setFolderPath] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [labelLoading, setLabelLoading] = useState(false);
  const [syncFeedback, setSyncFeedback] = useState(null);
  const syncInFlightRef = useRef(false);
  const skuSaveInFlightRef = useRef(false);
  const productWritesInFlightRef = useRef(0);
  const labelInFlightRef = useRef(false);

  const beginProductWrite = () => {
    productWritesInFlightRef.current += 1;
  };

  const endProductWrite = () => {
    productWritesInFlightRef.current = Math.max(
      0,
      productWritesInFlightRef.current - 1,
    );
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await axios.get(
          `${STOC_MANAGER_API}/products/${productId}`,
        );
        setProduct(res.data);
        setFormData(res.data);
      } catch (err) {
        console.error("Eroare la fetch detalii produs:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();

    const fetchFolderPath = async () => {
      try {
        const res = await axios.get(
          `${STOC_MANAGER_API}/products/${productId}/folder-path`,
        );
        setFolderPath(res.data);
      } catch (err) {
        console.error("Eroare la fetch folder path:", err);
      }
    };
    fetchFolderPath();
  }, [productId]);

  const fetchFolders = async () => {
    const res = await axios.get(`${STOC_MANAGER_API}/folders/previews`);
    setAvailableFolders(res.data);
  };

  const filteredFolders = availableFolders.filter((folder) =>
    folder.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const form = new FormData();
    form.append("image", file);

    beginProductWrite();
    try {
      // 1️⃣ Upload imagine în S3 fără să creeze produs nou
      const uploadRes = await axios.post(
        `${STOC_MANAGER_API}/images/upload-to-s3`,
        form,
      );
      const imageUrl = uploadRes.data.image_url;

      // 2️⃣ Actualizează produsul existent cu noul image_url
      await axios.patch(`${STOC_MANAGER_API}/products/${productId}/field`, {
        field: "image",
        value: imageUrl,
      });

      // 3️⃣ Actualizează starea locală
      setFormData((prev) => ({ ...prev, image_url: imageUrl }));
      setProduct((prev) => ({ ...prev, image_url: imageUrl }));
    } catch (err) {
      console.error("❌ Eroare la actualizare imagine:", err);
    } finally {
      endProductWrite();
    }
  };

  const handleSaveEdit = async () => {
    beginProductWrite();
    try {
      await axios.put(`${STOC_MANAGER_API}/products/${productId}`, {
        ...formData,
        image: formData.image_base64 || null,
      });
      setProduct({ ...product, ...formData });
      setEditOpen(false);
    } catch (err) {
      console.error("Eroare la actualizare produs:", err);
    } finally {
      endProductWrite();
    }
  };

  const handleClone = () => {
    const { id, ...cloned } = product;
    setFormData({ ...cloned });
    setEditOpen(true);
  };

  const handleSync = async () => {
    if (syncInFlightRef.current) {
      return;
    }
    if (skuSaveInFlightRef.current) {
      window.alert("Așteaptă finalizarea salvării SKU-ului, apoi apasă Sync.");
      return;
    }

    const normalizedProductId = String(productId ?? "").trim();
    const normalizedSku = String(product?.sku ?? "").trim();

    if (!normalizedProductId || !normalizedSku) {
      window.alert(
        normalizedProductId
          ? "Completează și salvează SKU-ul produsului, apoi apasă din nou Sync."
          : "ID-ul produsului lipsește. Reîncarcă pagina și încearcă din nou.",
      );
      return;
    }

    syncInFlightRef.current = true;
    setSyncLoading(true);
    setSyncFeedback(null);

    try {
      const { data } = await syncProductWithRetry(
        normalizedProductId,
        normalizedSku,
      );
      const syncUpdates = getSyncUpdates(data);
      if (Object.keys(syncUpdates).length) {
        setProduct((prev) => (prev ? { ...prev, ...syncUpdates } : prev));
        setFormData((prev) => ({ ...prev, ...syncUpdates }));
      }
      setSyncFeedback({
        severity: "success",
        message: `Produsul cu SKU ${normalizedSku} a fost sincronizat.`,
      });
    } catch (error) {
      console.error("Eroare la sincronizare:", error);
      setSyncFeedback({
        severity: "error",
        message: getErrorMessage(
          error,
          "Nu am reușit să sincronizăm produsul. Încearcă din nou.",
        ),
      });
    } finally {
      syncInFlightRef.current = false;
      setSyncLoading(false);
    }
  };

  const handleGenerateLabel = async () => {
    if (labelInFlightRef.current) {
      return;
    }
    if (
      productWritesInFlightRef.current > 0 ||
      skuSaveInFlightRef.current ||
      syncInFlightRef.current
    ) {
      window.alert(
        "Așteaptă finalizarea modificării produsului, apoi generează din nou eticheta.",
      );
      return;
    }
    if (!product?.sku || String(product.sku).trim() === "") {
      window.alert(
        "Completați mai întâi SKU-ul produsului înainte de a genera eticheta.",
      );
      return;
    }

    labelInFlightRef.current = true;
    setLabelLoading(true);
    try {
      const res = await axios.post(
        `${STOC_MANAGER_API}/products/${encodeURIComponent(productId)}/label`,
        null,
        { timeout: 50000 },
      );
      const { url } = res.data;

      // Deschide PDF-ul într-un tab nou (cel mai sigur comportament iOS)
      window.open(url, "_blank");

      // Opțional: ștergere după 2 minute
      setTimeout(() => {
        const filename = url.split("/").pop();
        axios
          .delete(`${STOC_MANAGER_API}/products/label/${filename}`)
          .catch((err) => {
            if (err.response?.status === 404) {
              console.warn(`🧹 PDF-ul a fost deja șters: ${filename}`);
            } else {
              console.error(
                "❌ Eroare la ștergerea fișierului PDF:",
                err.message,
              );
            }
          });
      }, 120000);
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.message ||
        "Eroare la generarea etichetei.";
      console.error("Eroare la generarea etichetei:", msg);
      window.alert(msg);
    } finally {
      labelInFlightRef.current = false;
      setLabelLoading(false);
    }
  };

  const EditableField = ({ label, value, fieldKey }) => {
    const [editing, setEditing] = useState(false);
    const [localValue, setLocalValue] = useState(value);
    const [saveError, setSaveError] = useState("");

    const saveChange = async () => {
      const isSkuField = fieldKey === "sku";
      const valueToSave = isSkuField
        ? String(localValue ?? "").trim()
        : localValue;

      if (isSkuField && !valueToSave) {
        setSaveError("Completează SKU-ul înainte de salvare.");
        return;
      }
      if (isSkuField && syncInFlightRef.current) {
        setSaveError(
          "Așteaptă finalizarea sincronizării înainte de a schimba SKU-ul.",
        );
        return;
      }
      if (isSkuField && skuSaveInFlightRef.current) {
        return;
      }

      setSaveError("");
      if (isSkuField) skuSaveInFlightRef.current = true;
      beginProductWrite();
      try {
        await axios.patch(
          `${STOC_MANAGER_API}/products/${productId}/field`,
          {
            field: fieldKey,
            value: valueToSave,
          },
          { timeout: 15000 },
        );
        setProduct((prev) => ({ ...prev, [fieldKey]: valueToSave }));
        setFormData((prev) => ({ ...prev, [fieldKey]: valueToSave }));
        setEditing(false);
      } catch (err) {
        console.error("Eroare la salvare:", err);
        setSaveError(
          getErrorMessage(
            err,
            fieldKey === "sku"
              ? "SKU-ul nu a putut fi salvat. Valoarea a fost păstrată; încearcă din nou."
              : "Modificarea nu a putut fi salvată. Încearcă din nou.",
          ),
        );
      } finally {
        endProductWrite();
        if (isSkuField) skuSaveInFlightRef.current = false;
      }
    };

    return (
      <Box mb={2}>
        {editing ? (
          <Box display="flex" alignItems="center">
            {fieldKey === "quantity" ? (
              <Box display="flex" alignItems="center" width="100%">
                <IconButton
                  onClick={() =>
                    setLocalValue((prev) => Math.max(0, Number(prev) - 1))
                  }
                >
                  <Typography fontSize={20}>−</Typography>
                </IconButton>
                <TextField
                  fullWidth
                  label={label}
                  value={localValue}
                  onChange={(e) => setLocalValue(e.target.value)}
                  size="small"
                  sx={{ mx: 1 }}
                />
                <IconButton
                  onClick={() => setLocalValue((prev) => Number(prev) + 1)}
                >
                  <Typography fontSize={20}>+</Typography>
                </IconButton>
                <IconButton onClick={saveChange}>
                  <CheckIcon color="success" />
                </IconButton>
              </Box>
            ) : (
              <>
                <TextField
                  fullWidth
                  label={label}
                  value={localValue}
                  onChange={(e) => {
                    setLocalValue(e.target.value);
                    setSaveError("");
                  }}
                  error={Boolean(saveError)}
                  helperText={saveError || undefined}
                  size="small"
                  sx={{ mr: 1 }}
                />
                <IconButton onClick={saveChange}>
                  <CheckIcon color="success" />
                </IconButton>
                {fieldKey === "web_link" && (
                  <IconButton onClick={() => setLocalValue("")}>
                    <Typography fontSize={20} color="error">
                      ×
                    </Typography>
                  </IconButton>
                )}
              </>
            )}
          </Box>
        ) : (
          <Box onClick={() => setEditing(true)} sx={{ cursor: "pointer" }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 0.5 }}
            >
              {label}
            </Typography>
            {fieldKey === "tags" && value ? (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {value.split(",").map((tag, i) => (
                  <Chip key={i} label={tag.trim()} />
                ))}
              </Box>
            ) : fieldKey === "web_link" && value ? (
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body1" color="text.primary">
                  {value}
                </Typography>
                <IconButton
                  href={value}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  sx={{ p: 0.5 }}
                >
                  <LaunchIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : fieldKey === "notes" ? (
              <Typography variant="body1" color="text.primary">
                {value || "-"}
              </Typography>
            ) : (
              <Typography
                fontWeight="bold"
                color="text.primary"
                sx={{ fontSize: fieldKey === "product_name" ? 20 : 14 }}
              >
                {fieldKey === "price" ? `${value} RON` : value || "-"}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  if (loading)
    return (
      <Container sx={{ pt: 4 }}>
        <CircularProgress />
      </Container>
    );
  if (!product)
    return (
      <Container sx={{ pt: 4 }}>
        <Typography>Produsul nu a fost găsit.</Typography>
      </Container>
    );

  const hasSyncSku = Boolean(String(product.sku ?? "").trim());

  return (
    <Box
      sx={{
        color: "#fff",
        "& .MuiTypography-root": { color: "#fff" },
        "& .MuiSvgIcon-root": { color: "#fff" },
        "& .MuiIconButton-root": { color: "#fff" },
        "& .circle-icon": { color: "#0B3D91" },
        "& .circle-icon .MuiSvgIcon-root": { color: "#0B3D91" },
        "& .MuiInputBase-input": { color: "#fff" },
        "& .MuiInputLabel-root": { color: "#fff" },
        "& .MuiFormLabel-root": { color: "#fff" },
        "& .MuiInputBase-input::placeholder": {
          color: "rgba(255,255,255,0.7)",
          opacity: 1,
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(255,255,255,0.6)",
        },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "#fff",
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
          {
            borderColor: "#fff",
          },
        "& .MuiChip-label": { color: "#fff" },
        "& .MuiButton-root": { color: "#fff" },
      }}
    >
      <style>
        {`
        .product-image-wrapper {
          position: relative;
          width: 100%;
          height: 300px;
          overflow: hidden;
          margin-bottom: 1px;
        }

        .product-image {
          width: 100%;
          height: 300px;
          object-fit: cover;
          object-position: center 67%;
        }
      `}
      </style>
      <Container disableGutters sx={{ pt: 2, px: "calc(3 * 8px - 30px)" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            Detalii produs
          </Typography>
          <IconButton onClick={() => navigate(-1)} size="small">
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box className="product-image-wrapper">
          <Box
            component="img"
            className="product-image"
            src={
              formData.image_url ||
              product.image_url ||
              "https://via.placeholder.com/600x300?text=Imagine+Indisponibila"
            }
            alt={product.product_name}
          />
          <IconButton
            component="label"
            sx={{
              position: "absolute",
              bottom: 8,
              right: 8,
              backgroundColor: "#fff",
              color: "#0B3D91",
              boxShadow: 1,
              "&:hover": {
                backgroundColor: "#f0f0f0",
              },
            }}
          >
            <EditIcon />
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleImageChange}
            />
          </IconButton>
        </Box>

        {/* Spațiu vizual între imagine și folder */}
        <Box sx={{ height: 8 }} />

        <Box
          display="flex"
          alignItems="center"
          sx={{ mt: 2, mb: 1 }}
          color="text.secondary"
        >
          <FolderOpenIcon fontSize="small" sx={{ mr: 1 }} />
          <Typography fontSize={16}>
            {folderPath?.layer1?.name}
            {folderPath?.layer1?.name && folderPath?.layer2?.name && " • "}
            {folderPath?.layer2?.name}
          </Typography>
        </Box>

        {/* Spațiu vizual între folder și titlu */}
        <Box sx={{ mb: 2 }}>
          <EditableField
            label="Titlu produs"
            value={product.product_name}
            fieldKey="product_name"
          />
        </Box>

        <Box
          sx={{
            borderTop: "1px solid #ccc",
            borderBottom: "1px solid #ccc",
            py: 2,
            my: 3,
          }}
        >
          <Box display="flex" justifyContent="space-around">
            <Box display="flex" flexDirection="column" alignItems="center">
              <IconButton
                size="large"
                color="inherit"
                className="circle-icon"
                onClick={() => {
                  fetchFolders();
                  setMoveDialogOpen(true);
                }}
                sx={{
                  backgroundColor: "#fff",
                  color: "#0B3D91",
                  boxShadow: 1,
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
              >
                <DriveFileMoveIcon sx={{ fontSize: 32 }} />
              </IconButton>
              <Typography variant="caption" sx={{ mt: 0.5 }}>
                Mută
              </Typography>
            </Box>
            <Box display="flex" flexDirection="column" alignItems="center">
              <IconButton
                size="large"
                color="inherit"
                className="circle-icon"
                onClick={handleSync}
                disabled={syncLoading}
                aria-label={
                  hasSyncSku
                    ? "Sincronizează produsul cu WooCommerce"
                    : "Completează SKU-ul pentru sincronizare"
                }
                sx={{
                  backgroundColor: "#fff",
                  color: "#0B3D91",
                  boxShadow: 1,
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#fff",
                    opacity: 0.75,
                  },
                }}
              >
                {syncLoading ? (
                  <CircularProgress size={28} sx={{ color: "#0B3D91" }} />
                ) : (
                  <AutorenewIcon sx={{ fontSize: 32 }} />
                )}
              </IconButton>
              <Typography variant="caption" sx={{ mt: 0.5 }}>
                {syncLoading
                  ? "Sincronizare..."
                  : hasSyncSku
                    ? "Sync"
                    : "SKU necesar"}
              </Typography>
            </Box>
            <Box display="flex" flexDirection="column" alignItems="center">
              <IconButton
                size="large"
                color="inherit"
                className="circle-icon"
                onClick={handleClone}
                sx={{
                  backgroundColor: "#fff",
                  color: "#0B3D91",
                  boxShadow: 1,
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 32 }} />
              </IconButton>
              <Typography variant="caption" sx={{ mt: 0.5 }}>
                Clonează
              </Typography>
            </Box>
            <Box display="flex" flexDirection="column" alignItems="center">
              <IconButton
                size="large"
                color="inherit"
                className="circle-icon"
                onClick={handleGenerateLabel}
                disabled={
                  labelLoading ||
                  syncLoading ||
                  !product?.sku ||
                  String(product.sku).trim() === ""
                }
                sx={{
                  backgroundColor: "#fff",
                  color: "#0B3D91",
                  boxShadow: 1,
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
              >
                {labelLoading ? (
                  <CircularProgress size={28} sx={{ color: "#0B3D91" }} />
                ) : (
                  <QrCode2Icon sx={{ fontSize: 32 }} />
                )}
              </IconButton>
              <Typography variant="caption" sx={{ mt: 0.5 }}>
                {labelLoading ? "Generare..." : "Etichetă"}
              </Typography>
            </Box>
          </Box>
        </Box>
        {syncFeedback && (
          <Alert
            severity={syncFeedback.severity}
            onClose={() => setSyncFeedback(null)}
            sx={{ mb: 2 }}
          >
            {syncFeedback.message}
          </Alert>
        )}

        <Grid container spacing={1} mb={2}>
          <Grid item xs={6}>
            <EditableField
              label="Preț"
              value={product.price}
              fieldKey="price"
            />
          </Grid>
          <Grid item xs={6}>
            <EditableField
              label="Cantitate"
              value={product.quantity}
              fieldKey="quantity"
            />
          </Grid>
          <Grid item xs={12}>
            <EditableField label="SKU" value={product.sku} fieldKey="sku" />
          </Grid>
        </Grid>

        <EditableField label="Tags" value={product.tags} fieldKey="tags" />
        <Box
          sx={{
            borderTop: "1px solid #ccc",
            pt: 2,
            borderBottom: "1px solid #ccc",
            pb: 1,
          }}
        >
          <EditableField
            label="Notițe"
            value={product.notes}
            fieldKey="notes"
          />
        </Box>
        <Box
          sx={{
            borderTop: "1px solid #ccc",
            pt: 2,
            borderBottom: "1px solid #ccc",
            pb: 1,
          }}
        >
          <EditableField
            label="Link"
            value={product.web_link}
            fieldKey="web_link"
          />
        </Box>

        <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth>
          <DialogTitle>Editează Produs</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              margin="dense"
              label="Nume produs"
              value={formData.product_name || ""}
              onChange={(e) =>
                setFormData({ ...formData, product_name: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="dense"
              label="SKU"
              value={formData.sku || ""}
              onChange={(e) =>
                setFormData({ ...formData, sku: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="dense"
              label="Preț"
              type="number"
              value={formData.price || ""}
              onChange={(e) =>
                setFormData({ ...formData, price: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="dense"
              label="Cantitate"
              type="number"
              value={formData.quantity || ""}
              onChange={(e) =>
                setFormData({ ...formData, quantity: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="dense"
              label="Tags"
              value={formData.tags || ""}
              onChange={(e) =>
                setFormData({ ...formData, tags: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="dense"
              label="Link Web"
              value={formData.web_link || ""}
              onChange={(e) =>
                setFormData({ ...formData, web_link: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="dense"
              label="Notițe"
              multiline
              value={formData.notes || ""}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
            />
            <Button
              component="label"
              variant="outlined"
              fullWidth
              sx={{ mt: 2 }}
            >
              Încarcă o imagine nouă
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageChange}
              />
            </Button>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)}>Anulează</Button>
            <Button variant="contained" onClick={handleSaveEdit}>
              Salvează
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={moveDialogOpen}
          onClose={() => setMoveDialogOpen(false)}
          fullWidth
        >
          <DialogTitle>Mută în alt folder</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Caută folder..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 2 }}
            />
            {filteredFolders.map((folder) => (
              <Box
                key={folder.id}
                onClick={() => setTargetFolderId(folder.id)}
                sx={{
                  p: 1,
                  my: 1,
                  borderRadius: 2,
                  border:
                    folder.id === targetFolderId
                      ? "2px solid #1976d2"
                      : "1px solid #ccc",
                  backgroundColor:
                    folder.id === targetFolderId ? "#e3f2fd" : "#f9f9f9",
                  cursor: "pointer",
                }}
              >
                <Typography fontWeight="bold">{folder.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {folder.totalProducts || 0} produse •{" "}
                  {folder.totalValue?.toLocaleString() || 0} RON
                </Typography>
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMoveDialogOpen(false)}>Anulează</Button>
            <Button
              variant="contained"
              disabled={!targetFolderId}
              onClick={async () => {
                await axios.put(
                  `${STOC_MANAGER_API}/products/${product.id}/move`,
                  {
                    folderId: targetFolderId,
                  },
                );
                const selected = availableFolders.find(
                  (f) => f.id === targetFolderId,
                );
                const updated = {
                  ...product,
                  folder_id: targetFolderId,
                  folder_name: selected?.name,
                  folder_title: selected?.name,
                };
                setProduct(updated);
                setFormData(updated);
                setMoveDialogOpen(false);
              }}
            >
              Mută
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
      {/* Delete Button and Dialog */}
      <Box display="flex" justifyContent="center" mt={5}>
        <IconButton
          onClick={() => setDeleteDialogOpen(true)}
          sx={{
            backgroundColor: "#fff",
            color: "#0B3D91",
            boxShadow: 2,
            "&:hover": {
              backgroundColor: "#ffe6e6",
            },
          }}
        >
          <DeleteIcon />
        </IconButton>
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirmare ștergere</DialogTitle>
        <DialogContent>
          <Typography>Dorești să ștergi acest produs?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Nu</Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              await axios.delete(`${STOC_MANAGER_API}/products/${product.id}`);
              navigate(`/folder/${product.folder_id}`);
            }}
          >
            Da, șterge
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MobileProductsInventoryDetails;
